import React from "react";
import {cn} from "../../utils/cn";

/**
 * Standard page header. Same template everywhere:
 *   [title + subtitle]                           [actions →]
 *
 *   <PageHeader title="Users" subtitle="…" actions={<Button>…</Button>} />
 *
 * Optionally renders breadcrumbs above the title via the `breadcrumbs` prop
 * (an array of {label, href?} — last item is treated as current).
 */
export default function PageHeader({title, subtitle, breadcrumbs, actions, className, children}) {
  return (
    <header className={cn("px-6 lg:px-8 py-5 lg:py-6 bg-white border-b border-ink-100", className)}>
      <div className="max-w-[1440px] mx-auto flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-ink-500 mb-1">
              {breadcrumbs.map((b, i) => {
                const isLast = i === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={i}>
                    {b.href && !isLast ? (
                      <a href={b.href} className="hover:text-ink-700">{b.label}</a>
                    ) : (
                      <span className={isLast ? "text-ink-700 font-medium" : ""}>{b.label}</span>
                    )}
                    {!isLast && <span aria-hidden="true" className="text-ink-300">/</span>}
                  </React.Fragment>
                );
              })}
            </nav>
          )}
          <h1 className="text-3xl font-bold tracking-tight text-ink-900">{title}</h1>
          {subtitle && <p className="text-base text-ink-500 mt-1 max-w-2xl">{subtitle}</p>}
          {children}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}
